import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete, MatOption } from '@angular/material/autocomplete';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { FocusService } from 'app/core/services/focus.service';
import { HighlightService } from 'app/core/services/highlight.service';
import { PickerStateService } from 'app/core/services/picker-state.service';
import { HighlightType } from 'app/shared/enums/highlight-type.enum';
import { AutoCompleteResultProjection } from 'app/shared/prothesis-api-projections/auto-complete-result.projection';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { PickerState } from '../../../states/picker-state';
import { ComboModel } from '../../combo/combo-model';
import { ElementModel } from './element.model';
import { PickerDialogModel } from './picker-dialog/picker-dialog-model';
import { PickerDialogComponent } from './picker-dialog/picker-dialog.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-picker',
  styleUrls: ['./picker.component.scss'],
  templateUrl: './picker.component.html',
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    MatAutocompleteTrigger,
    ReactiveFormsModule,
    MatAutocomplete,
    MatOption,
    MatButton,
    MatIconButton,
    MatTooltip,
    MatIcon,
  ],
})
export class PickerComponent implements OnChanges, OnDestroy, OnInit {
  @ViewChild('_inputText') public _inputText: ElementRef;

  // Label of the Add Button
  @Input() public addButtonLabel: string;

  // Tooltip Label of the Add Icon Button
  @Input() public addIconButtonTooltip: string;

  @Input() public combo2DefaultValue: ComboModel | null | undefined;
  @Input() public combo2DisableFlag: boolean;
  @Input() public combo2MatLabel: string;
  @Input() public combo2NullableOption: string;
  @Input() public combo2Options: ComboModel[] | null;

  @Input() public comboDefaultValue: ComboModel | null | undefined;
  @Input() public comboDisableFlag = false;
  @Input() public comboMatLabel: string;
  @Input() public comboNullableOption: string;
  @Input() public comboOptions: ComboModel[];

  @Input() public disableCreationFlag = false;
  @Input() public disableFlag: boolean | string = false;

  @Input() public elementCreationIcon = 'svg_search_guide';
  @Input() public elementCreationLink: string;
  @Input() public elementCreationLinkLabel: string | null;
  @Input() public elementCreationMessage = 'Aucun résultat.';

  // Element to display in the picker
  @Input() public elements: ElementModel[];
  @Input() public existenceFlag: boolean;

  // Indicates whethear to highlight the parts of the picker list.
  @Input() public highlightCol1GroupFlag = false;
  @Input() public highlightCol1GroupType = HighlightType.String;
  @Input() public highlightCol1InfoFlag = false;
  @Input() public highlightCol1LabelFlag = false;
  @Input() public highlightCol1SubgroupFlag = false;
  @Input() public highlightCol2GroupFlag = false;
  @Input() public highlightCol2GroupType = HighlightType.String;
  @Input() public highlightCol2InfoFlag = false;
  @Input() public highlightCol2LabelFlag = false;
  @Input() public highlightCol2SubgroupFlag = false;
  @Input() public highlightCol3LabelFlag = false;
  @Input() public highlightCol3LabelType = HighlightType.String;

  @Input() public icon = 'autorenew';

  // Event raise when the autocomplete input change (only if auto complete feature was enabled)
  @Output() public inputChanged = new EventEmitter<string>();

  @Input() public loadedFlag: boolean;

  // Text displayed on the code place holder
  @Input() public matLabel: string;

  // Indicates the possibilitys to have a multiple selection.
  @Input() public multipleSelectionFlag = true;

  @Output() public pickerDialogAfterClosedEvent = new EventEmitter<void>();

  // The selected element is sent through this output
  @Output() public selectElement = new EventEmitter<ElementModel>();

  @Input() public showInputFlag = true;

  // Indicates wether we are displaying the research part or not
  @Input() public showPickerFlag = true;

  @Input() public showTextFilter2Flag = false;

  @Input() public showTextInputFlag = true;

  // The picker state.
  @Input() public state: PickerState;

  // Text displayed on the text filters mat-label & max length and text filters default value
  @Input() public textFilter2DefaultValue: string | null;
  @Input() public textFilter2MatLabel: string;
  @Input() public textFilter2Maxlength = 255;
  @Input() public textFilterDefaultValue: string | null | undefined;
  @Input() public textFilterMatLabel = 'Rechercher dans la liste';
  @Input() public textFilterMaxlength = 255;

  // The length of the total number of element that are being paginated. Defaulted to 0.
  @Input() public totalCount = 0;

  // The length of the truncated content if specified. Default is null (others values are small, medium, large)
  @Input() public truncatedContentWidth: string;

  // Determines if the pin is unpined when the picker is opened.
  @Input() public unpinnedByDefaultFlag = true;

  // picker state updated are sent through this output.
  @Output() public updateState = new EventEmitter<PickerState>();

  // Value of the uuid selected in case of autocomplete feature enabled
  @Output() public uuidSelected = new EventEmitter<string>();

  // Icon used to go to the drop down list picker research
  public addIcon = 'add_circle_outline';

  public dialogRef: MatDialogRef<PickerDialogComponent, unknown>;

  public firstLoadFlag = true;

  // The form control associated with the code input
  public formControl: FormControl<string | null> | null = new FormControl<string>('');

  // Result of the autoComplete Api search
  private _autoCompleteResultList: AutoCompleteResultProjection[] = [];
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _dialog = inject(MatDialog);
  private _focusService = inject(FocusService);
  private _highlightService = inject(HighlightService);
  private _isLoadingAutocomplete = false;
  // Memorize the last searched value
  private _lastInputValue = '';
  private _lastSearchValue = '';
  private _ngZone = inject(NgZone);
  // A flag to know if the "enter" key was pressed
  private _pendingEnterAction = false;
  private _pickerStateService = inject(PickerStateService);

  private _unsubscribeAll$: Subject<void> = new Subject();

  @Input()
  public set autoCompleteResultList(value: AutoCompleteResultProjection[]) {
    this._autoCompleteResultList = value;
    this._isLoadingAutocomplete = false;

    if (
      this._pendingEnterAction &&
      value.length > 0 &&
      !this._isLoadingAutocomplete &&
      this.formControl!.value?.trim() === this._lastSearchValue
    ) {
      this.executeEnterAction();
    }
  }

  public get autoCompleteResultList(): AutoCompleteResultProjection[] {
    return this._autoCompleteResultList;
  }

  public clickHandler(): boolean {
    this.openPickerDialog();
    return false;
  }

  public closedHandler(): void {
    this._focusService.setTimeoutOutsideAngular(() => {
      this._ngZone.run(() => {
        this.autoCompleteResultList = [];
        this._pickerStateService.autocompleteOpenedFlag = false;

        this._cleanupAutocompleteOverlay();

        this._changeDetectorRef.detectChanges();
      });
    });
  }

  // Handle the "enter" key to add special conditions before doing it
  public handleEnterKey($event: Event): void {
    $event.preventDefault();
    const currentInputValue = this.formControl!.value?.trim() || '';

    if (this._autoCompleteResultList.length > 0) {
      this._isLoadingAutocomplete = false;
    }

    if (
      this._autoCompleteResultList.length > 0 &&
      !this._isLoadingAutocomplete &&
      currentInputValue === this._lastSearchValue &&
      currentInputValue.length > 0
    ) {
      this._focusService.setTimeoutOutsideAngular(() => {
        this._ngZone.run(() => {
          this.executeEnterAction();
        });
      });
      return;
    }

    if (currentInputValue.length > 0) {
      this._pendingEnterAction = true;
    }
  }

  public highlight(content: string, highlightType = HighlightType.String): string | null | undefined {
    return this._highlightService.highlight(content, highlightType);
  }

  public isFocused(): boolean {
    return this._inputText && this._inputText.nativeElement === document.activeElement;
  }

  public ngOnChanges(): void {
    if (this.dialogRef && this.dialogRef.componentInstance && this.dialogRef.componentInstance.pickerDialog) {
      this.dialogRef.componentInstance.pickerDialog.combo2DefaultValue = this.combo2DefaultValue!;
      this.dialogRef.componentInstance.pickerDialog.combo2DisableFlag = this.combo2DisableFlag;
      this.dialogRef.componentInstance.pickerDialog.combo2MatLabel = this.combo2MatLabel;
      this.dialogRef.componentInstance.pickerDialog.combo2NullableOption = this.combo2NullableOption;
      this.dialogRef.componentInstance.pickerDialog.combo2Options = this.combo2Options;
      this.dialogRef.componentInstance.pickerDialog.textFilter2Maxlength = this.textFilter2Maxlength;
      this.dialogRef.componentInstance.pickerDialog.textFilter2MatLabel = this.textFilter2MatLabel;
      this.dialogRef.componentInstance.pickerDialog.textFilter2Flag = this.showTextFilter2Flag;
      this.dialogRef.componentInstance.pickerDialog.textFilterMaxlength = this.textFilterMaxlength;
      this.dialogRef.componentInstance.pickerDialog.textFilterMatLabel = this.textFilterMatLabel;
      this.dialogRef.componentInstance.pickerDialog.disableCreationFlag = this.disableCreationFlag;
      this.dialogRef.componentInstance.pickerDialog.titleLabel = this.addIconButtonTooltip;
      this.dialogRef.componentInstance.pickerDialog.comboOptions = this.comboOptions;

      // Signal pushes rely on the default Object.is equality: callers must provide a fresh
      // array reference on each load (all picker wrappers reassign `elements` before pushing).
      // An in-place mutation of the same array would not notify the dialog.
      this.dialogRef.componentInstance.elements.set(this.elements);
      this.dialogRef.componentInstance.pickerDialog.existenceFlag = this.existenceFlag;
      this.dialogRef.componentInstance.loadedFlag.set(this.loadedFlag);
      this.dialogRef.componentInstance.pickerDialog.comboDefaultValue = this.comboDefaultValue!;
      this.dialogRef.componentInstance.pickerDialog.comboDisableFlag = this.comboDisableFlag;
      this.dialogRef.componentInstance.pickerDialog.comboMatLabel = this.comboMatLabel;
      this.dialogRef.componentInstance.pickerDialog.comboNullableOption = this.comboNullableOption;
      this.dialogRef.componentInstance.pickerDialog.state = this.state;
      this.dialogRef.componentInstance.totalCount.set(this.totalCount);
      this.dialogRef.componentInstance.pickerDialog.truncatedContentWidth = this.truncatedContentWidth;
      if (this.firstLoadFlag) {
        this.dialogRef.componentInstance.focusOnTextFilter();
        this.firstLoadFlag = false;
      }
    }

    this._disableOrEnablePicker(this.disableFlag);
  }

  public ngOnDestroy(): void {
    this._pickerStateService.autocompleteOpenedFlag = false;

    this._cleanupAutocompleteOverlay();

    // Unsubscribe from all subscriptions
    this._unsubscribeAll$.next();
    this._unsubscribeAll$.complete();
  }

  public ngOnInit(): void {
    // Change Icon when single selection
    if (!this.multipleSelectionFlag) {
      this.addIcon = 'check_circle_outline';
    }

    this.reset();
    if (this.showTextInputFlag) {
      this.formControl!.valueChanges.pipe(debounceTime(200))
        .pipe(takeUntil(this._unsubscribeAll$))
        .subscribe((input) => {
          if (input !== null) {
            const trimmedInput = input.trim();
            if (trimmedInput.length === 0) {
              this.autoCompleteResultList = [];
              this._lastSearchValue = '';
              this._lastInputValue = '';
              this._pendingEnterAction = false;
            } else {
              // Avoid calling the API in loop for the same value
              if (trimmedInput === this._lastInputValue) {
                return;
              }

              if (trimmedInput.length < this._lastSearchValue.length) {
                this._pendingEnterAction = false;
              }
              this._isLoadingAutocomplete = true;
              this._lastSearchValue = trimmedInput;
              this._lastInputValue = trimmedInput;
              this._highlightService.compileFilter(trimmedInput);
              this.inputChanged.emit(trimmedInput);
            }
          }
        });
    }
  }

  public openHandler(): void {
    this._pickerStateService.autocompleteOpenedFlag = true;
  }

  public openPickerDialog(): void {
    this._forceCloseAutocompleteOverlay();

    // Capture the current input value before resetting
    const currentInputValue = this.formControl!.value?.trim() || '';

    // open PickerDialog
    this.reset();

    const pickerDialog = new PickerDialogModel();
    pickerDialog.combo2DefaultValue = this.combo2DefaultValue!;
    pickerDialog.combo2DisableFlag = this.combo2DisableFlag;
    pickerDialog.combo2MatLabel = this.combo2MatLabel;
    pickerDialog.combo2NullableOption = this.combo2NullableOption;
    pickerDialog.combo2Options = this.combo2Options;
    pickerDialog.comboDefaultValue = this.comboDefaultValue!;
    pickerDialog.comboDisableFlag = this.comboDisableFlag;
    pickerDialog.comboMatLabel = this.comboMatLabel;
    pickerDialog.comboNullableOption = this.comboNullableOption;
    pickerDialog.comboOptions = this.comboOptions;
    pickerDialog.disableCreationFlag = this.disableCreationFlag;
    pickerDialog.elementCreationIcon = this.elementCreationIcon;
    pickerDialog.elementCreationLink = this.elementCreationLink;
    pickerDialog.elementCreationLinkLabel = this.elementCreationLinkLabel;
    pickerDialog.elementCreationMessage = this.elementCreationMessage;
    pickerDialog.existenceFlag = this.existenceFlag;
    pickerDialog.highlightCol1GroupFlag = this.highlightCol1GroupFlag;
    pickerDialog.highlightCol1GroupType = this.highlightCol1GroupType;
    pickerDialog.highlightCol1InfoFlag = this.highlightCol1InfoFlag;
    pickerDialog.highlightCol1LabelFlag = this.highlightCol1LabelFlag;
    pickerDialog.highlightCol1SubgroupFlag = this.highlightCol1SubgroupFlag;
    pickerDialog.highlightCol2GroupFlag = this.highlightCol2GroupFlag;
    pickerDialog.highlightCol2GroupType = this.highlightCol2GroupType;
    pickerDialog.highlightCol2InfoFlag = this.highlightCol2InfoFlag;
    pickerDialog.highlightCol2LabelFlag = this.highlightCol2LabelFlag;
    pickerDialog.highlightCol2SubgroupFlag = this.highlightCol2SubgroupFlag;
    pickerDialog.highlightCol3LabelFlag = this.highlightCol3LabelFlag;
    pickerDialog.highlightCol3LabelType = this.highlightCol3LabelType;
    pickerDialog.multipleSelectionFlag = this.multipleSelectionFlag;
    pickerDialog.state = this.state;
    pickerDialog.state.filterSelectedContainerUuid = this.comboDefaultValue?.key;
    pickerDialog.state.filter2SelectedContainerUuid = this.combo2DefaultValue?.key;

    if (this.showTextInputFlag && this.showInputFlag) {
      pickerDialog.state.textFilter = currentInputValue || this._lastSearchValue;
    } else {
      pickerDialog.state.textFilter2 = this.textFilter2DefaultValue;
      pickerDialog.state.textFilter = this.textFilterDefaultValue;
    }

    pickerDialog.textFilter2Maxlength = this.textFilter2Maxlength;
    pickerDialog.textFilter2MatLabel = this.textFilter2MatLabel;
    pickerDialog.textFilter2Flag = this.showTextFilter2Flag;
    pickerDialog.textFilterMaxlength = this.textFilterMaxlength;
    pickerDialog.textFilterMatLabel = this.textFilterMatLabel;
    pickerDialog.titleLabel = this.addIconButtonTooltip;
    pickerDialog.truncatedContentWidth = this.truncatedContentWidth;
    pickerDialog.unpinnedByDefaultFlag = this.unpinnedByDefaultFlag;

    this.updateState.emit(this.state);

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = false;
    dialogConfig.data = pickerDialog;

    this.dialogRef = this._dialog.open(PickerDialogComponent, dialogConfig);

    // Output doesn't need to be unsubscribed.
    this.dialogRef.componentInstance.updateState.subscribe((data) => {
      this.updateState.emit(data);
    });

    // Output doesn't need to be unsubscribed.
    this.dialogRef.componentInstance.selectElement.subscribe((data) => {
      this.selectElement.emit(data);
    });

    // AfterClosed event is completed when the matdialog is closed.
    // https://stackoverflow.com/questions/58198544/angular-dialogref-unsubscribe-do-i-need-to-unsubscribe-from-afterclosed
    this.dialogRef.afterClosed().subscribe(() => {
      this.firstLoadFlag = true;
      this._resetAutocompleteState();
      this.pickerDialogAfterClosedEvent.emit();
    });
  }

  public optionSelectedHandler(matAutocompleteSelectedEvent: MatAutocompleteSelectedEvent): void {
    this._pendingEnterAction = false;

    this._focusService.setTimeoutOutsideAngular(() => {
      if (this._inputText?.nativeElement) {
        this._inputText.nativeElement.blur();

        this._ngZone.run(() => {
          this.autoCompleteResultList = [];
          this._lastSearchValue = '';
          this._lastInputValue = '';
          this.uuidSelected.emit(matAutocompleteSelectedEvent.option.id);
          this.formControl!.setValue('', { emitEvent: false });
          this._changeDetectorRef.detectChanges();
        });
      }
    });
  }

  public reset(): void {
    this.autoCompleteResultList = [];
    this.formControl!.setValue('');
    this._pickerStateService.autocompleteOpenedFlag = false;
    this._pendingEnterAction = false;
    this._lastSearchValue = '';
    this._lastInputValue = '';
    this._cleanupAutocompleteOverlay();

    this._changeDetectorRef.detectChanges();
  }

  public setFocus(): void {
    // Some component using the picker may use *ngIf, so setTimeout is required.
    this._focusService.setTimeoutOutsideAngular(() => {
      this._inputText?.nativeElement?.focus();
    });
  }

  // Cleanup any remaining autocomplete overlay artifacts
  private _cleanupAutocompleteOverlay(): void {
    const overlayContainer = document.querySelector('.cdk-overlay-container');
    if (overlayContainer) {
      // Hide any autocomplete panels that might still be present
      const autocompletePanels = overlayContainer.querySelectorAll('.mat-mdc-autocomplete-panel');
      autocompletePanels.forEach((panel) => {
        // Remove classes that indicate the panel is open
        panel.classList.remove('mdc-menu-surface--open');
        panel.classList.add('mat-mdc-autocomplete-hidden');

        // If the panel is still visible, hide it instead of removing
        if ((panel as HTMLElement).offsetParent !== null) {
          (panel as HTMLElement).style.display = 'none';
        }
      });

      // Remove any empty overlay panes
      const emptyOverlayPanes = overlayContainer.querySelectorAll('.cdk-overlay-pane:empty');
      emptyOverlayPanes.forEach((pane) => {
        pane.remove();
      });
    }
  }

  private _disableOrEnablePicker(disableFlag: boolean | string): void {
    if (disableFlag) {
      this.formControl!.disable();
    } else {
      this.formControl!.enable();
    }
  }

  // Force close any open autocomplete overlay by hiding it instead of removing it
  private _forceCloseAutocompleteOverlay(): void {
    const overlayContainer = document.querySelector('.cdk-overlay-container');
    if (overlayContainer) {
      const autocompletePanels = overlayContainer.querySelectorAll('.mat-mdc-autocomplete-panel');
      autocompletePanels.forEach((panel) => {
        // Hide the panel instead of removing it to allow proper re-display
        panel.classList.remove('mdc-menu-surface--open');
        panel.classList.add('mat-mdc-autocomplete-hidden');

        // If the panel is still visible, hide it
        if ((panel as HTMLElement).offsetParent !== null) {
          (panel as HTMLElement).style.display = 'none';
        }
      });

      // Remove any empty overlay panes
      const emptyOverlayPanes = overlayContainer.querySelectorAll('.cdk-overlay-pane:empty');
      emptyOverlayPanes.forEach((pane) => {
        pane.remove();
      });
    }

    // Clear the autocomplete results and trigger change detection
    this.autoCompleteResultList = [];
    this._changeDetectorRef.detectChanges();
  }

  // Reset autocomplete state after dialog is closed
  private _resetAutocompleteState(): void {
    this._pickerStateService.autocompleteOpenedFlag = false;
    this.autoCompleteResultList = [];
    this.formControl!.setValue('', { emitEvent: false });
    this._cleanupAutocompleteOverlay();
    this._changeDetectorRef.detectChanges();
  }

  // Fire the optionSelectedHandler when the "enter" key was pressed and API call finished
  private executeEnterAction(): void {
    // If the user hit "enter" fast enought to go here (API haven't finished yet) we assume he want the first option
    const firstOption = this._autoCompleteResultList[0];
    if (!firstOption) {
      return;
    }

    const event = {
      option: {
        id: firstOption.uuid,
        value: firstOption.label,
      },
    } as MatAutocompleteSelectedEvent;

    this._focusService.setTimeoutOutsideAngular(() => {
      this._ngZone.run(() => {
        this.optionSelectedHandler(event);
      });
    });
  }
}
